// @file Surface sections store
import { captureException } from '@@/bits/error_tracker'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { $ } from '@@/bits/jquery'
import { MAX_SECTION_TITLE_LENGTH, MIN_SECTION_TITLE_LENGTH } from '@@/bits/numbers'
import sectionSorter from '@@/bits/section_sorter'
import { JsonAPIResource, saveWall } from '@padlet/arvo'
import { WishArrangement } from '@padlet/arvo/lib/types'
import { defineStore, storeToRefs } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceSettingsStore } from 'pinia/surface_settings'
import { useWindowSizeStore } from 'pinia/window_size'
import PadletApi from 'surface/padlet_api'
import type { Id, Section } from 'types'
import { computed, ref } from 'vue'
import { SnackbarNotificationType } from 'vuexstore/modules/global_snackbar'

export const useSurfaceSectionsStore = defineStore('surfaceSections', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia
  const globalSnackbarStore = useGlobalSnackbarStore()

  // values from other stores
  const { isSmallerThanTabletLandscape } = storeToRefs(useWindowSizeStore())
  const { canUseSections } = storeToRefs(useSurfaceStore())
  const { previewAttributes } = storeToRefs(useSurfaceSettingsStore())

  // state
  const sectionRecentlyCreatedId = ref<Id | null>(null)
  const sectionBeingRenamedId = ref<Id | null>(null)
  const sectionUnderActionId = ref<Id | null>(null)
  const sectionUnderActionRightClickCoordinates = ref<{ x: number; y: number } | null>(null)
  const isASectionDragging = ref(false)

  // getters
  const xSectionRenameModal = computed(() => sectionBeingRenamedId.value != null && isSmallerThanTabletLandscape.value)
  const sectionUnderAction = computed(() => getSectionById(sectionUnderActionId.value))
  const defaultSectionId = computed(() => surfaceVuexStore?.getters['section/defaultSectionId'])
  const sectionById = computed<(sectionId: Id | null) => Section | null>(
    () => surfaceVuexStore?.getters['section/sectionById'] ?? (() => null),
  )
  const sortedSections = computed<Array<Required<Section>>>(() => {
    return surfaceVuexStore?.getters['section/sortedSections'] ?? []
  })
  const sortedSectionIds = computed(() => {
    return sortedSections.value.filter((s) => s.id != null).map((s) => s.id)
  })
  const hasFetchedSections = computed<boolean>(() =>
    canUseSections.value ? surfaceVuexStore?.getters['section/areSectionsFetched'] : true,
  )

  const originalWishArrangement = computed<WishArrangement | undefined>(
    () => surfaceVuexStore?.state.wall.wish_arrangement,
  )

  const currentWishArrangement = computed<WishArrangement | undefined>(() => previewAttributes.value.wish_arrangement)

  // getter functions
  function getSectionById(sectionId: Id | null): Section | null {
    return sectionById.value(sectionId)
  }

  // actions

  /**
   * Create a new section and give it a default name: `Section 1`, `Section 2`,... `Section n`
   * where `n` is the number of sections in the wall.
   * @param sortIndex If not provided, the new section will be created at the bottom of the wall.
   */
  async function createSectionWithDefaultName(sortIndex?: number, gapSize?: number): Promise<void> {
    try {
      const title = __('Section %{number}', {
        number: ((surfaceVuexStore?.getters['section/numOfSections'] ?? 0) as number) + 1,
      })
      const sectionData = await PadletApi.WallSection.create({
        wall_id: surfaceVuexStore?.getters.wallId,
        title,
        sort_index: sortIndex ?? sectionSorter.getBottomSortIndex(),
        gap_size: gapSize ?? null,
      })

      const data = (sectionData.data as JsonAPIResource<Section>).attributes
      surfaceVuexStore?.commit('section/ADD_SECTION', data)
      sectionRecentlyCreatedId.value = data.id ?? null
      sectionBeingRenamedId.value = data.id ?? null
      void surfaceVuexStore?.dispatch('post/updateMostRecentlyTouchedSection', data.id, { root: true })
      void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState', null, { root: true })
    } catch (e) {
      void surfaceVuexStore?.dispatch(
        'snackbar',
        {
          notificationType: SnackbarNotificationType.error,
          message: __('Failed to create section'),
        },
        { root: true },
      )
      captureException(e)
    }
  }

  function createSectionBefore(sectionId: Id): void {
    const { sortIndex, gapSize } = sectionSorter.getNewIndexBefore($(`#section-${sectionId}`))
    void createSectionWithDefaultName(sortIndex, gapSize)
  }

  function createSectionAfter(sectionId: Id): void {
    const { sortIndex, gapSize } = sectionSorter.getNewIndexAfter($(`#section-${sectionId}`))
    void createSectionWithDefaultName(sortIndex, gapSize)
  }

  async function changeDefaultSection(sectionId: Id): Promise<void> {
    try {
      await saveWall({
        ...surfaceVuexStore?.state.wall,
        wish_arrangement: {
          ...surfaceVuexStore?.state.wall.wish_arrangement,
          default_section_id: sectionId,
        },
      })

      surfaceVuexStore?.commit('section/SET_DEFAULT_SECTION_ID', sectionId)

      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Default section changed'),
      })
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Failed to update default section'),
      })
      captureException(e)
    }
  }

  function editSectionTitle(payload: { sectionId: Id; title: string }): boolean {
    const { sectionId, title } = payload
    const trimmedTitle = title.trim()
    const isTitleTooShort = trimmedTitle.length < MIN_SECTION_TITLE_LENGTH
    const isTitleTooLong = trimmedTitle.length > MAX_SECTION_TITLE_LENGTH
    if (isTitleTooShort) {
      void surfaceVuexStore?.dispatch(
        'alertDialog/openAlertDialog',
        {
          title: __('Section name is too short'),
          body: __('Section name must be at least %{min} characters long.', { min: MIN_SECTION_TITLE_LENGTH }),
          closeButtonText: __('OK'),
          shouldFadeIn: true,
        },
        { root: true },
      )
      return false
    }

    if (isTitleTooLong) {
      void surfaceVuexStore?.dispatch(
        'alertDialog/openAlertDialog',
        {
          title: __('Section name is too long'),
          body: __('Section name must be at most %{max} characters long.', { max: MAX_SECTION_TITLE_LENGTH }),
          closeButtonText: __('OK'),
          shouldFadeIn: true,
        },
        { root: true },
      )
      return false
    }

    void surfaceVuexStore?.dispatch('section/changeSectionTitle', { sectionId, title: trimmedTitle })
    void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState', null, { root: true })
    return true
  }
  function startRenamingSection(sectionId: Id): void {
    sectionBeingRenamedId.value = sectionId
  }
  function stopRenamingSection(): void {
    sectionBeingRenamedId.value = null
  }

  function askToDeleteSection(sectionId: Id | null): void {
    if (sectionId == null) return
    if (sortedSections.value.length === 1) {
      void surfaceVuexStore?.dispatch(
        'alertDialog/openAlertDialog',
        {
          title: __('Section cannot be deleted'),
          body: __('At least one section is required. Please create a new section if you want to delete this one.'),
          closeButtonText: __('OK'),
          shouldFadeIn: true,
        },
        { root: true },
      )
    } else {
      void surfaceVuexStore?.dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          title: __('Delete this section?'),
          body: __('Are you sure you want to delete this section and all its posts? This cannot be undone!'),
          confirmButtonText: __('Delete'),
          cancelButtonText: __('Cancel'),
          isConfirmActionDanger: true,
          afterConfirmActions: [{ type: 'section/deleteSection', payload: { sectionId } }],
        },
        { root: true },
      )
    }
  }

  function sortSection(payload: { sectionId: Id; sortIndex: number; gapSize: number }): void {
    const { sectionId, sortIndex, gapSize } = payload
    const sectionToChange = sectionById.value(sectionId)
    if (sectionToChange == null) {
      throw new Error("Section doesn't exist")
    }
    const isSameIndex = sectionToChange.sort_index === sortIndex
    const isStuck = gapSize <= 4
    if (isSameIndex && !isStuck) return

    const updatedObject = { ...sectionToChange, sort_index: sortIndex }
    void surfaceVuexStore?.commit('section/UPDATE_SECTION', { id: sectionId, obj: updatedObject })
    void PadletApi.WallSection.update({ ...updatedObject, gap_size: gapSize })
    void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState', null, { root: true })
  }

  function moveSectionBefore(sectionId: Id): void {
    const index = sortedSectionIds.value.indexOf(sectionId)

    // No-op if already the first section
    if (index <= 0) return

    const section = sortedSections.value[index]
    let sortIndex = section.sort_index
    let gapSize: number | null = null

    if (index === 1) {
      // If is second-to-first section, use top sort index
      // to become the new first section
      sortIndex = sectionSorter.getTopSortIndex()
      const currentFirstSection = sortedSections.value[0]
      gapSize = currentFirstSection.sort_index - sortIndex
    } else {
      const newBeforeSection = sortedSections.value[index - 2]
      const newAfterSection = sortedSections.value[index - 1]
      sortIndex = (newAfterSection.sort_index + newBeforeSection.sort_index) / 2
      gapSize = (newAfterSection.sort_index - newBeforeSection.sort_index) / 2
    }

    sortSection({ sectionId, sortIndex, gapSize })
  }

  function moveSectionAfter(sectionId: Id): void {
    const index = sortedSectionIds.value.indexOf(sectionId)

    // No-op if already the last section
    if (index === -1 || index >= sortedSectionIds.value.length - 1) return

    const section = sortedSections.value[index]
    let sortIndex = section.sort_index
    let gapSize: number | null = null

    if (index === sortedSections.value.length - 2) {
      // If is second-to-last section, use bottom sort index
      // to become the new last section
      sortIndex = sectionSorter.getBottomSortIndex()
      const currentLastSection = sortedSections.value[sortedSections.value.length - 1]
      gapSize = sortIndex - currentLastSection.sort_index
    } else {
      const newBeforeSection = sortedSections.value[index + 1]
      const newAfterSection = sortedSections.value[index + 2]
      sortIndex = (newAfterSection.sort_index + newBeforeSection.sort_index) / 2
      gapSize = (newAfterSection.sort_index - newBeforeSection.sort_index) / 2
    }

    sortSection({ sectionId, sortIndex, gapSize })
  }

  return {
    // state
    sectionRecentlyCreatedId,
    sectionBeingRenamedId,
    sectionUnderActionId,
    sectionUnderActionRightClickCoordinates,
    isASectionDragging,

    // getters
    sectionUnderAction,
    defaultSectionId,
    sortedSections,
    sortedSectionIds,
    xSectionRenameModal,
    hasFetchedSections,
    currentWishArrangement,
    originalWishArrangement,

    // getter functions
    getSectionById,

    // actions
    changeDefaultSection,
    createSectionWithDefaultName,
    createSectionBefore,
    createSectionAfter,
    editSectionTitle,
    startRenamingSection,
    stopRenamingSection,
    askToDeleteSection,
    sortSection,
    moveSectionBefore,
    moveSectionAfter,
  }
})
